import React, { useEffect, useRef, useState } from 'react';
import { loadGoogleMapsApi } from '../utils/googleGeocode';

const DEFAULT_CENTER = { lat: -34.6037, lng: -58.3816 };
const DEFAULT_ZOOM = 11;
const PICKED_ZOOM = 16;

/**
 * Búsqueda con sugerencias (Places), al elegir una: lat/lng + mapa con marcador.
 */
export default function AddressAutocompleteMap({ onPlaceSelected }) {
  const inputRef = useRef(null);
  const mapRef = useRef(null);
  const acRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerInstanceRef = useRef(null);
  const onPickedRef = useRef(onPlaceSelected);

  onPickedRef.current = onPlaceSelected;

  const [hint, setHint] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [apiReady, setApiReady] = useState(false);

  const setMarkerAndMap = (g, map, loc) => {
    if (markerInstanceRef.current) {
      markerInstanceRef.current.setPosition(loc);
      markerInstanceRef.current.setMap(map);
    } else {
      markerInstanceRef.current = new g.Marker({
        position: loc,
        map,
        animation: g.Animation.DROP,
      });
    }
    map.panTo(loc);
    map.setZoom(PICKED_ZOOM);
    setTimeout(() => g.event.trigger(map, 'resize'), 150);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadGoogleMapsApi();
        if (cancelled || !inputRef.current || !mapRef.current) return;
        const g = window.google.maps;
        const ac = new g.places.Autocomplete(inputRef.current, {
          fields: ['formatted_address', 'geometry', 'name'],
          types: ['address'],
        });
        acRef.current = ac;

        const map = new g.Map(mapRef.current, {
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        });
        mapInstanceRef.current = map;
        g.event.addListenerOnce(map, 'idle', () => {
          if (!cancelled) g.event.trigger(map, 'resize');
        });
        if (!cancelled) setApiReady(true);

        ac.addListener('place_changed', () => {
          const place = ac.getPlace();
          if (!place?.geometry?.location) {
            setHint('Elige una sugerencia de la lista para fijar la ubicación');
            return;
          }
          setHint(null);
          const loc = place.geometry.location;
          const lat = loc.lat();
          const lng = loc.lng();
          const address = place.formatted_address || place.name || inputRef.current?.value || '';
          setMarkerAndMap(g, map, loc);
          onPickedRef.current?.({ address, lat, lng });
        });

        map.addListener('click', (e) => {
          const loc = e?.latLng;
          if (!loc) return;
          setHint(null);
          const lat = loc.lat();
          const lng = loc.lng();
          setMarkerAndMap(g, map, loc);
          const geocoder = new g.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            const resolvedAddress =
              status === 'OK' && results?.[0]
                ? results[0].formatted_address
                : `Punto seleccionado (${lat.toFixed(6)}, ${lng.toFixed(6)})`;
            if (inputRef.current) inputRef.current.value = resolvedAddress;
            onPickedRef.current?.({ address: resolvedAddress, lat, lng });
          });
        });
      } catch (e) {
        if (!cancelled) {
          const m = e?.message || 'No se pudo cargar el mapa';
          setLoadError(m);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (acRef.current && window.google?.maps) {
        window.google.maps.event.clearInstanceListeners(acRef.current);
        acRef.current = null;
      }
      mapInstanceRef.current = null;
      markerInstanceRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!apiReady || !mapInstanceRef.current) return;
    const t = setTimeout(() => {
      try {
        window.google.maps.event.trigger(mapInstanceRef.current, 'resize');
      } catch {
        /* empty */
      }
    }, 400);
    return () => clearTimeout(t);
  }, [apiReady]);

  if (loadError) {
    return <p className="text-sm text-red-600">{loadError}</p>;
  }

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-gray-600">Dirección</label>
      <input
        ref={inputRef}
        type="text"
        className="input-mobile w-full"
        placeholder="Escribe y elige una dirección (como en Google Maps)"
        autoComplete="off"
        aria-label="Buscar dirección con sugerencias"
      />
      {hint && <p className="text-xs text-amber-700">{hint}</p>}
      <p className="text-xs text-gray-500">Puedes elegir una sugerencia o hacer click directo en el mapa. En ambos casos se guardan dirección, latitud y longitud.</p>
      <div
        ref={mapRef}
        className="w-full h-52 min-h-[208px] rounded-lg border border-gray-200 overflow-hidden bg-gray-100"
        role="application"
        aria-label="Vista del mapa de la sede"
      />
    </div>
  );
}
